import { useCallback, useEffect, useState } from 'react';

import { validateDecimalInput, validateDigitsInput } from '../../../utils/formatUtils';
import useToast from '../../../hooks/useToast';
import { getClasificationApprover, getOperationalDetails } from '../../../services/workReport';

const useProgramPoint = (props) => {
  const {
    setFieldValue,
    values,
    notificaPorCSA,
    tipoAsignacion,
    idParteDiario,
    evento,
    formName = '',
    indexPP
  } = props;
  const { setNewToast } = useToast();
  const [costOptions, setCostOptions] = useState([]);

  const getProgramPointsInScope = () => {
    if (Array.isArray(values?.programPoints)) {
      return values.programPoints;
    }

    const locationMatch = formName?.match(/locations\[(\d+)\]\.?/);

    if (locationMatch) {
      const locationIndex = Number(locationMatch[1]);

      return values?.locations?.[locationIndex]?.programPoints || [];
    }

    return values?.programPoints || [];
  };

  const hasClasificationSet = (programPoint) => {
    const hasOptions =
      Array.isArray(programPoint?.optionsClasification) &&
      programPoint?.optionsClasification?.length > 0;

    return hasOptions;
  };

  const hasClasificationValue = (programPoint) =>
    Boolean(
      programPoint?.clasification?.value ||
        programPoint?.clasification?.id ||
        programPoint?.clasification?.label ||
        programPoint?.clasification?.nombre
    );

  const getClasificationKey = (item) =>
    item?.value || item?.id || item?.nombre || item?.label || '';

  const normalizeClasificationOption = (option) => {
    const optionValue = getClasificationKey(option);

    if (!optionValue) return null;

    return {
      ...option,
      nombre: option?.nombre || optionValue,
      id: option?.id || optionValue,
      label: option?.label || optionValue,
      value: option?.value || optionValue
    };
  };

  const mergeClasificationOptions = (options, optionToAdd) => {
    const merged = [...(Array.isArray(options) ? options : [])];
    const normalizedOption = normalizeClasificationOption(optionToAdd);

    if (!normalizedOption) return merged;

    const mergedKeys = new Set(merged.map((item) => getClasificationKey(item)));
    const optionKey = getClasificationKey(normalizedOption);

    if (!mergedKeys.has(optionKey)) {
      merged.push(normalizedOption);
    }

    return merged;
  };

  const mapClasificationOption = (item) => {
    if (typeof item === 'string') {
      return {
        nombre: item,
        id: item,
        label: item,
        value: item
      };
    }

    return {
      ...item,
      nombre: item?.sigla || item?.nombre,
      id: item?.sigla || item?.nombre,
      label: item?.sigla || item?.nombre,
      value: item?.sigla || item?.nombre
    };
  };

  const getDefaultClasification = (option) => ({
    ...option,
    nombre: option?.value || option?.nombre,
    id: option?.value || option?.id || option?.nombre,
    label: option?.value || option?.label || option?.nombre,
    value: option?.value || option?.id || option?.nombre
  });

  const setProgramPointClasificationOptions = (
    programPointsInScope,
    idxPP,
    classificationOptions,
    firstOption
  ) => {
    let selectedClasification = programPointsInScope?.[idxPP]?.clasification;
    const hasCurrentClasification = hasClasificationValue(programPointsInScope?.[idxPP]);

    if (!hasCurrentClasification && firstOption) {
      const defaultClasification = getDefaultClasification(firstOption);

      selectedClasification = defaultClasification;
      setFieldValue(`${formName}programPoints[${idxPP}].clasification`, defaultClasification);
    }

    const selectedKey = getClasificationKey(selectedClasification);
    const filteredOptions = (classificationOptions || []).filter(
      (option) => getClasificationKey(option) !== selectedKey
    );

    setFieldValue(`${formName}programPoints[${idxPP}].optionsClasification`, filteredOptions);
  };

  const fetchClasification = async (val, indexesToUpdate = []) => {
    try {
      const targetPhase = val?.fase;

      const indexes = Array.isArray(indexesToUpdate) ? indexesToUpdate : [indexesToUpdate];

      if (!targetPhase) {
        indexes.forEach((idxPP) => {
          setFieldValue(`${formName}programPoints[${idxPP}].optionsClasification`, []);
        });

        return;
      }

      const clasification = await getClasificationApprover(targetPhase, evento);

      const classificationOptions = (Array.isArray(clasification) ? clasification : []).map(
        mapClasificationOption
      );

      const firstOption = classificationOptions?.[0];
      const programPointsInScope = getProgramPointsInScope();

      indexes.forEach((idxPP) => {
        setProgramPointClasificationOptions(
          programPointsInScope,
          idxPP,
          classificationOptions,
          firstOption
        );
      });
    } catch (error) {
      setNewToast({ type: 'ERROR', message: error.message });
    }
  };

  const getPhaseEventByIndex = (programPointsInScope = []) =>
    programPointsInScope
      .map((programPoint, idxPP) => ({
        index: idxPP,
        fase: programPoint?.cost?.fase,
        evento
      }))
      .filter((item) => item?.fase);

  const groupPhaseEventByKey = (phaseEventByIndex = []) =>
    phaseEventByIndex.reduce((acc, item) => {
      const key = `${item.evento || ''}-${item.fase}`;
      const currentGroup = acc[key] || {
        phase: item.fase,
        event: item.evento,
        indexes: []
      };

      currentGroup.indexes.push(item.index);
      acc[key] = currentGroup;

      return acc;
    }, {});

  const shouldFetchClasificationForIndexes = (programPointsInScope, indexes = []) =>
    indexes.some((idxPP) => !hasClasificationSet(programPointsInScope[idxPP]));

  const fetchClasificationByProgramPoints = async (programPointsInScope = []) => {
    const phaseEventByIndex = getPhaseEventByIndex(programPointsInScope);
    const groupedByPhaseEvent = groupPhaseEventByKey(phaseEventByIndex);

    await Promise.all(
      Object.values(groupedByPhaseEvent).map(async ({ phase, indexes }) => {
        const shouldFetchClasification = shouldFetchClasificationForIndexes(
          programPointsInScope,
          indexes
        );

        if (!shouldFetchClasification) return;

        const firstIndex = indexes[0];
        const baseCost = programPointsInScope[firstIndex]?.cost;

        if (!baseCost) return;

        await fetchClasification({ ...baseCost, fase: phase }, indexes);
      })
    );
  };

  const fetchCode = async (val, index) => {
    const body = {
      tipoAsignacion,
      claseCosto: val.costoClase,
      fase: val.fase
      // codigo: 'T',
      // subcodigo: '140'
    };

    const response = await getOperationalDetails(idParteDiario, body);

    const adapterCode = response?.codigo?.map((code, indexCode) => ({
      nombre: code,
      id: indexCode + 1
    }));

    setFieldValue(`${formName}programPoints[${index}].optionsCode`, adapterCode);
  };

  const fetchSubCodes = async (val, index) => {
    try {
      const body = {
        tipoAsignacion,
        claseCosto: values?.programPoints[index]?.cost?.costoClase,
        fase: values?.programPoints[index]?.cost?.fase,
        codigo: val.nombre
        // subcodigo: '140'
      };
      const response = await getOperationalDetails(idParteDiario, body);

      const dataFilterSubCode = response?.subcodigo?.map((sub, indexSub) => ({
        nombre: sub,
        id: indexSub + 1
      }));

      setFieldValue(`${formName}programPoints[${index}].optionsSubCode`, dataFilterSubCode);
    } catch (error) {
      setNewToast({ type: 'ERROR', message: error.message });
    }
  };

  const fetchCost = async (index) => {
    try {
      const body = {
        tipoAsignacion
        // claseCosto: 'EQP',
        // fase: '10b',
        // codigo: 'T',
        // subcodigo: '140'
      };

      if (index === 0 && values?.habilitaVentana) {
        const response = await getOperationalDetails(idParteDiario, body);
        const adapterData = response?.costoClase?.map((cost) => ({
          ...cost,
          nombreCorto: cost.costoClase,
          nombre: cost.combinacion,
          id: `${cost.costoClase} | ${cost.fase}`
        }));

        setCostOptions(adapterData || []);

        const programPointsInScope = getProgramPointsInScope();

        programPointsInScope?.forEach((item, idxPP) => {
          setFieldValue(`${formName}programPoints[${idxPP}].costOptions`, adapterData || []);
        });

        await fetchClasificationByProgramPoints(programPointsInScope);
      }
    } catch (error) {
      setNewToast({ type: 'ERROR', message: error.message });
    }
  };

  const fetchDetails = async (val, index) => {
    try {
      const body = {
        tipoAsignacion,
        claseCosto: values?.programPoints[index]?.cost?.costoClase,
        fase: values?.programPoints[index]?.cost?.fase,
        codigo: values?.programPoints[index]?.code?.label,
        subcodigo: val?.label
      };
      const response = await getOperationalDetails(idParteDiario, body);

      setFieldValue(`${formName}programPoints[${index}].detail`, response.descripcion || []);
    } catch (error) {
      setNewToast({ type: 'ERROR', message: error.message });
    }
  };

  const recalculateSum = (value = 0) => {
    let formattedTotalValue = value.toFixed(3).replace(/0+$/, '');

    if (formattedTotalValue.endsWith('.')) {
      formattedTotalValue = formattedTotalValue.slice(0, -1);
    }

    return formattedTotalValue;
  };
  const handleAddPP = () => {
    const firstPP = values?.programPoints && values?.programPoints[0];

    setFieldValue(`${formName}programPoints`, [
      ...values.programPoints,
      { ...firstPP, quantity: '', points: '' }
    ]);
  };

  const handleSuprPP = (pLIndex) => {
    const newProgramPoints = values.programPoints.filter((_, index) => index !== pLIndex);

    let newTotalValue;

    if (newProgramPoints?.length > 0) {
      newTotalValue = newProgramPoints.reduce(
        (acc, curr) => parseFloat(acc) + parseFloat(curr.quantity || 0),
        0
      );
    } else {
      newTotalValue = 0;
    }
    const valueTotalFixed = recalculateSum(newTotalValue);

    setFieldValue(`${formName}programPoints`, newProgramPoints);
    setFieldValue(`${formName}totalValue`, valueTotalFixed);
  };

  const handleChangeQuantity = useCallback(
    async (val, index) => {
      const { value } = val.target;

      const isZeroValue = value !== '' && !Number.isNaN(Number(value)) && Number(value) === 0;

      if (isZeroValue) {
        setFieldValue(`${formName}programPoints[${index}].subCode`, null);
        setFieldValue(`${formName}programPoints[${index}].code`, null);
        setFieldValue(`${formName}programPoints[${index}].detail`, null);
        setFieldValue(`${formName}programPoints[${index}].clasification`, null);
        setFieldValue(`${formName}programPoints[${index}].cost`, null);
        setFieldValue(`${formName}programPoints[${index}].points`, '');
        setFieldValue(`${formName}programPoints[${index}].optionsClasification`, []);
        setFieldValue(`${formName}programPoints[${index}].optionsSubCode`, []);
        setFieldValue(`${formName}programPoints[${index}].optionsCode`, []);
      }

      if (validateDecimalInput(value, 3)) {
        const newTotalValue = values.programPoints?.reduce((acc, curr, i) => {
          if (i === index) {
            if (value === '') return acc;

            return parseFloat(acc) + parseFloat(value);
          }
          if (curr?.quantity === '') return parseFloat(acc);

          return parseFloat(acc) + parseFloat(curr.quantity || 0);
        }, 0);

        const valueTotalFixed = recalculateSum(newTotalValue);

        setFieldValue(`${formName}programPoints[${index}].quantity`, value);
        setFieldValue(`${formName}totalValue`, valueTotalFixed);
      }
    },
    [values, setFieldValue, formName]
  );

  const handleChangePoints = useCallback(
    async (val, index) => {
      const { value } = val.target;

      if (value === '0') return;

      if (validateDigitsInput(value, 4)) {
        setFieldValue(`${formName}programPoints[${index}].points`, value);
      }
    },
    [setFieldValue, formName]
  );

  const handleChangeCode = async (val, index) => {
    const currentCode = values?.programPoints?.[index]?.code;

    if (
      currentCode &&
      (currentCode.value === val?.value ||
        currentCode.id === val?.id ||
        currentCode.label === val?.label)
    ) {
      return;
    }

    setFieldValue(`${formName}programPoints[${index}].subCode`, null);
    setFieldValue(`${formName}programPoints[${index}].detail`, '');

    setFieldValue(`${formName}programPoints[${index}].code`, {
      ...val,
      nombre: val.label,
      id: val.label,
      label: val.label,
      value: val.label
    });

    fetchSubCodes(val, index);
  };

  const handleChangeSubCode = async (val, index) => {
    const currentSubCode = values?.programPoints?.[index]?.subCode;

    if (currentSubCode && (currentSubCode.value === val?.value || currentSubCode.id === val?.id)) {
      return;
    }

    setFieldValue(`${formName}programPoints[${index}].detail`, '');

    setFieldValue(`${formName}programPoints[${index}].subCode`, {
      ...val,
      nombre: val.label,
      id: val.label,
      label: val.label,
      value: val.label
    });

    fetchDetails(val, index);
  };

  const handleChangeCost = async (val, index) => {
    setFieldValue(`${formName}programPoints[${index}].subCode`, '');
    setFieldValue(`${formName}programPoints[${index}].code`, '');
    setFieldValue(`${formName}programPoints[${index}].detail`, '');
    setFieldValue(`${formName}programPoints[${index}].clasification`, '');

    setFieldValue(`${formName}programPoints[${index}].cost`, {
      ...val,
      nombre: val.nombreCorto,
      nombreLargo: val.nombre,
      id: val.value,
      label: `${val.costoClase} - ${val.fase}`,
      value: val.value
    });
    fetchCode(val, index);

    const programPointsInScope = getProgramPointsInScope();

    const indexesWithSamePhase = programPointsInScope
      .map((programPoint, idxPP) => {
        // values may still be stale for the edited row, so use the newly selected phase there
        const phase = idxPP === index ? val?.fase : programPoint?.cost?.fase;

        return phase === val?.fase ? idxPP : null;
      })
      .filter((idxPP) => idxPP !== null);

    const indexesToUpdate = Array.from(new Set([...(indexesWithSamePhase || []), index]));

    fetchClasification(val, indexesToUpdate);
  };

  const handleChangeClasification = async (val, index) => {
    const selectedValue = val?.value || val?.id || val?.nombre || val?.label;
    const programPointsInScope = getProgramPointsInScope();
    const previousClasification = programPointsInScope?.[index]?.clasification;

    const valuesClasification = {
      ...val,
      nombre: selectedValue,
      id: selectedValue,
      label: selectedValue,
      value: selectedValue
    };

    setFieldValue(`${formName}programPoints[${index}].clasification`, valuesClasification);

    const currentOptions = programPointsInScope?.[index]?.optionsClasification || [];
    const optionsWithPreviousSelected = mergeClasificationOptions(
      currentOptions,
      previousClasification
    );
    const filteredOptions = optionsWithPreviousSelected.filter(
      (option) => (option?.value || option?.id || option?.nombre || option?.label) !== selectedValue
    );

    setFieldValue(`${formName}programPoints[${index}].optionsClasification`, filteredOptions);
  };

  const handleFirstDetails = async (index) => {
    if (values?.programPoints[index]?.cost && values?.programPoints[index]?.code) {
      await fetchCode(values?.programPoints[index].cost, index);
    }
  };

  useEffect(() => {
    if (idParteDiario) {
      fetchCost(indexPP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificaPorCSA, indexPP]);

  useEffect(() => {
    handleFirstDetails(indexPP);
    const newTotalValue = values?.programPoints.reduce((acc, curr) => {
      if (curr?.quantity === '') return parseFloat(acc);

      return parseFloat(acc) + parseFloat(curr.quantity || 0);
    }, 0);

    const valueTotalFixed = recalculateSum(newTotalValue);

    setFieldValue(`${formName}totalValue`, Number(valueTotalFixed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexPP]);

  return {
    handleSuprPP,
    handleChangeQuantity,
    handleAddPP,
    handleChangePoints,
    handleChangeCode,
    handleChangeSubCode,
    handleChangeCost,
    handleChangeClasification,
    fetchDetails,
    fetchCode,
    costOptions: values?.programPoints[indexPP]?.costOptions || costOptions || []
  };
};

export default useProgramPoint;
